// Every card-rules-text transform in one place.
//
// This module imports NOTHING on purpose. scripts/card-rules-text.test.mjs
// loads it under `node --experimental-strip-types`, where Node ESM demands
// file extensions while TypeScript forbids `.ts` extensions in source. A .ts
// module that imports another project module therefore cannot be tested. Keep
// this file a leaf.
//
// One Piece text reaches us as TCGplayer's `extDescription` HTML
// (updater/internal/cards/onepiece_mapping.go), so it carries <br>, <strong>,
// <em>, <a> and marketplace <span style="color:red"> blurbs. Cards matched to
// Bandai's official catalogue get clean text instead, so both shapes have to
// survive the same pass. Riftbound and Star Wars Unlimited carry <br> too.

// ---------------------------------------------------------------- shared ---

// <br> arrives beside a real newline ("...Counter.\n<br>[On Play]..."), and
// ReplacedSymbolsOracleText renders into a whitespace-pre-line paragraph that
// already turns that newline into a break — so honoring both double-spaces the
// card. Collapse each <br> plus its surrounding horizontal whitespace to one
// newline, then cap runs at a single blank line. Feeds without <br>
// (Scryfall/MTG) are returned untouched.
const SOURCE_BREAK = /[^\S\n]*\n?[^\S\n]*<br\s*\/?>[^\S\n]*\n?[^\S\n]*/gi;

export function collapseSourceBreaks(text: string): string {
  if (!/<br/i.test(text)) return text;
  return text
    .replace(/\r/g, "")
    .replace(SOURCE_BREAK, "\n")
    .replace(/\n{3,}/g, "\n\n");
}

// TCGplayer marketplace copy, not rules text. Two flavours: a red <span>
// ("DISCLAIMER: This card is a reprint...") and a plain trailing sentence
// ("Disclaimer: This card is considered <strong>Not Tournament Legal</strong>.").
// Legality is already shown in the dialog's own panel, and the trailing flavour
// is inaccurate anyway — those cards are eternal-legal.
const DISCLAIMER_SPAN =
  /\s*<span[^>]*color:\s*red[^>]*>\s*disclaimer:.*?<\/span>/gis;
const TRAILING_DISCLAIMER = /\n*^[^\S\n]*Disclaimer:[\s\S]*$/im;

// Fail closed: only strip the disclaimer tail when no ability token follows it,
// so a feed that interleaves a disclaimer mid-text never loses rules text.
function stripTrailingDisclaimer(text: string): string {
  const match = TRAILING_DISCLAIMER.exec(text);
  if (!match) return text;
  if (/\[[^\]\n]{1,45}\]/.test(match[0])) return text;
  return text.slice(0, match.index);
}

// ------------------------------------------------- One Piece: display -------

// Only these tags occur in the One Piece feed. Everything else inside angle
// brackets is content an HTML parser would silently swallow: attributes
// ("<Slash> attribute cards"), a bare URL, and mangled markup such as
// "<You may rest 1 of your ... cards:</strong>" whose opening tag lost its
// name — that one deletes a whole rules clause.
//
// The lookahead is (?=[\s/>]) rather than \b on purpose: \b holds before a
// colon, so "<strong:You may rest 1 card:</strong>" would parse as a valid
// <strong> tag and destroy its text. Requiring whitespace, "/" or ">" right
// after the tag name closes that hole.
const NON_TAG_ANGLE_BRACKET =
  /<(?!\/?(?:br|strong|em|span|a|i|b|u|p)(?=[\s/>])[^>]*>)/gi;

export function escapeNonTagAngleBrackets(text: string): string {
  return text.replace(NON_TAG_ANGLE_BRACKET, "&lt;");
}

export function sanitizeOnePieceRulesText(text: string): string {
  const cleaned = text
    .replace(/\r/g, "")
    .replace(DISCLAIMER_SPAN, "")
    .replace(NON_TAG_ANGLE_BRACKET, "&lt;")
    .replace(SOURCE_BREAK, "\n")
    .replace(/\n{3,}/g, "\n\n");
  return stripTrailingDisclaimer(cleaned).trim();
}

// -------------------------------------------- all games: plain text ---------

const HTML_TAG = /<\/?[A-Za-z][^>]*>/g;
const NAMED_ENTITY = /&(?:lt|gt|quot|amp|nbsp|#39|#x27);/gi;
const ENTITY_VALUES: Readonly<Record<string, string>> = {
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&amp;": "&",
  "&nbsp;": " ",
  "&#39;": "'",
  "&#x27;": "'",
};

// Server-rendered JSON-LD (app/sets/.../page.tsx -> cardGraph.ts) uses card
// text verbatim as a Product description, so raw <strong>/<br> and marketplace
// disclaimers are served to search engines. Structured data wants a single
// plain-text line, not markup.
export function toPlainCardText(text: string): string {
  if (!text) return "";
  const cleaned = escapeNonTagAngleBrackets(collapseSourceBreaks(text)).replace(
    DISCLAIMER_SPAN,
    ""
  );
  return stripTrailingDisclaimer(cleaned)
    .replace(HTML_TAG, "")
    .replace(NAMED_ENTITY, (m) => ENTITY_VALUES[m.toLowerCase()] ?? m)
    .replace(/\s+/g, " ")
    .trim();
}

// ------------------------------------------ One Piece: token vocabulary -----

export type TokenRole = "trigger" | "keyword" | "limit" | "cost";

// Square-bracketed tokens carry two unrelated meanings in One Piece text:
// gameplay mechanics ([On Play]) and card names ([Monkey.D.Luffy]). Only the
// mechanics are badged; anything off this whitelist stays plain text, so card
// names and future tokens read correctly rather than being mislabelled.
//
// Keys are normalized (see normalizeToken) so printed variants such as
// "[On play]", "[Activate: Main]", "[On K.O]" and "[DON!!×1]" all resolve.
//
// DO NOT SYNC WITH THE UPDATER. opKeywordCanonical in
// updater/internal/cards/onepiece_keywords.go is deliberately NARROWER: it
// feeds the keyword FACET and excludes timing modifiers and DON!! costs as
// filter noise. This list feeds DISPLAY, where they are worth reading.
// Widening the Go list to match would degrade the facet.
export const TOKEN_ROLES: Readonly<Record<string, TokenRole>> = {
  // Effect triggers — when the ability happens.
  "on play": "trigger",
  "on k.o": "trigger",
  "on block": "trigger",
  "when attacking": "trigger",
  "on your opponent's attack": "trigger",
  "end of your turn": "trigger",
  trigger: "trigger",
  counter: "trigger",
  main: "trigger",
  "activate:main": "trigger",
  // Static keywords — what the card does.
  blocker: "keyword",
  rush: "keyword",
  "rush:character": "keyword",
  "double attack": "keyword",
  banish: "keyword",
  unblockable: "keyword",
  // Timing limits — when you may use it.
  "once per turn": "limit",
  "your turn": "limit",
  "opponent's turn": "limit",
  // DON!! attachment cost.
  "don!!x1": "cost",
  "don!!x2": "cost",
  "don!!x3": "cost",
};

// Printed ink is part of the card content, not the site theme. Keep the color
// pair on the rendered element so a missing/stale generated utility cannot
// leave white keyword text on a transparent background.
export interface RulesTokenAppearance {
  className?: string;
  style: {
    backgroundColor: string;
    color: string;
    clipPath?: string;
    WebkitClipPath?: string;
    borderRadius?: string;
  };
}

// Reference: https://en.onepiece-cardgame.com/cardlist/
const ONE_PIECE_BLUE: RulesTokenAppearance = {
  style: { backgroundColor: "#0079ad", color: "#fff", borderRadius: "0.2em" },
};
const ONE_PIECE_RED: RulesTokenAppearance = {
  style: { backgroundColor: "#be3560", color: "#fff", borderRadius: "0.2em" },
};
const ONE_PIECE_KEYWORD: RulesTokenAppearance = {
  className: "rounded-none px-2",
  style: {
    backgroundColor: "#ad5809",
    color: "#fff",
    clipPath:
      "polygon(0.45em 0, calc(100% - 0.45em) 0, 100% 50%, calc(100% - 0.45em) 100%, 0.45em 100%, 0 50%)",
    WebkitClipPath:
      "polygon(0.45em 0, calc(100% - 0.45em) 0, 100% 50%, calc(100% - 0.45em) 100%, 0.45em 100%, 0 50%)",
  },
};
const ONE_PIECE_TRIGGER: RulesTokenAppearance = {
  className: "rounded-none pr-2",
  style: {
    backgroundColor: "#f9df26",
    color: "#000",
    clipPath: "polygon(0 0, 100% 0, calc(100% - 0.5em) 100%, 0 100%)",
    WebkitClipPath: "polygon(0 0, 100% 0, calc(100% - 0.5em) 100%, 0 100%)",
  },
};
const ONE_PIECE_COST: RulesTokenAppearance = {
  className: "ring-1 ring-inset ring-white/40",
  style: { backgroundColor: "#191919", color: "#fff", borderRadius: "0.2em" },
};

export function onePieceTokenAppearance(
  bracketed: string
): RulesTokenAppearance | undefined {
  const role = tokenRole(bracketed);
  if (!role) return undefined;
  const token = normalizeToken(bracketed.slice(1, -1));
  if (token === "trigger") return ONE_PIECE_TRIGGER;
  if (token === "counter" || token === "once per turn") return ONE_PIECE_RED;
  if (role === "cost") return ONE_PIECE_COST;
  if (role === "keyword") return ONE_PIECE_KEYWORD;
  return ONE_PIECE_BLUE;
}

// Mirrors the canonical-key derivation in
// updater/internal/cards/onepiece_keywords.go:52 so both sides agree on what
// counts as the same token, even though the whitelists differ in width.
export function normalizeToken(token: string): string {
  return token
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/×/g, "x")
    .replace(/\s*:\s*/g, ":")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\.+$/, "")
    .replace(/^(don!!)\s*x\s*/, "$1x");
}

export function tokenRole(bracketed: string): TokenRole | undefined {
  return TOKEN_ROLES[normalizeToken(bracketed.slice(1, -1))];
}

export const BRACKET_TOKEN = /(\[[^\]\n]{1,45}\])/g;

// ------------------------------------------ Riftbound: token vocabulary -----

// Unlike One Piece, every square-bracketed token in the Riftbound feed is a
// gameplay mechanic — there are no card-name brackets. Most are parameterized
// ("[SHIELD 3]", "[EMPOWER 2 and 1 Fury]", "[EQUIP Calm]"), so tokens classify
// by their leading word rather than by exact match. Words off this map stay
// plain text so feed quirks are never mislabelled.
const RIFTBOUND_TOKEN_ROLES: Readonly<Record<string, TokenRole>> = {
  // Play timing and triggered labels.
  reaction: "trigger",
  action: "trigger",
  deathknell: "trigger",
  predict: "trigger",
  // Static keywords, parameterized or not.
  empowered: "keyword",
  ganking: "keyword",
  hidden: "keyword",
  deflect: "keyword",
  accelerate: "keyword",
  temporary: "keyword",
  tank: "keyword",
  assault: "keyword",
  ambush: "keyword",
  weaponmaster: "keyword",
  shield: "keyword",
  mighty: "keyword",
  stun: "keyword",
  legion: "keyword",
  buff: "keyword",
  vision: "keyword",
  "quick-draw": "keyword",
  hunt: "keyword",
  burn: "keyword",
  unique: "keyword",
  backline: "keyword",
  // Level thresholds.
  level: "limit",
  // Parameterized cost verbs.
  empower: "cost",
  equip: "cost",
  flow: "cost",
  add: "cost",
  repeat: "cost",
};

function riftboundTokenKey(bracketed: string): string {
  return normalizeToken(bracketed.slice(1, -1))
    .split(/[\s,]/, 1)[0]
    .replace(/\d+$/, "");
}

export function riftboundTokenRole(bracketed: string): TokenRole | undefined {
  return RIFTBOUND_TOKEN_ROLES[riftboundTokenKey(bracketed)];
}

// Reference: https://playriftbound.com/en-us/card-gallery/
// Printed labels do not follow the trigger/keyword/cost classification.
const RIFTBOUND_NEUTRAL: RulesTokenAppearance = {
  style: { backgroundColor: "#686868", color: "#fff" },
};
const RIFTBOUND_TIMING: RulesTokenAppearance = {
  style: { backgroundColor: "#007d72", color: "#fff" },
};
const RIFTBOUND_COMBAT: RulesTokenAppearance = {
  style: { backgroundColor: "#bd326d", color: "#fff" },
};
const RIFTBOUND_PASSIVE: RulesTokenAppearance = {
  style: { backgroundColor: "#a9c638", color: "#202415" },
};
const RIFTBOUND_TOKEN_APPEARANCES: Readonly<
  Record<string, RulesTokenAppearance>
> = {
  action: RIFTBOUND_TIMING,
  reaction: RIFTBOUND_TIMING,
  hidden: RIFTBOUND_TIMING,
  accelerate: RIFTBOUND_TIMING,
  ambush: RIFTBOUND_TIMING,
  flow: RIFTBOUND_TIMING,
  assault: RIFTBOUND_COMBAT,
  shield: RIFTBOUND_COMBAT,
  tank: RIFTBOUND_COMBAT,
  empowered: RIFTBOUND_PASSIVE,
  deflect: RIFTBOUND_PASSIVE,
  ganking: RIFTBOUND_PASSIVE,
  deathknell: RIFTBOUND_PASSIVE,
};

export function riftboundTokenAppearance(
  bracketed: string
): RulesTokenAppearance | undefined {
  if (!riftboundTokenRole(bracketed)) return undefined;
  return (
    RIFTBOUND_TOKEN_APPEARANCES[riftboundTokenKey(bracketed)] ??
    RIFTBOUND_NEUTRAL
  );
}
